/**
 * 라이더 주정산내역 타입 정의
 * 기반 테이블: vr_rider_acct_weekly
 */
import { formatDateToDisplay } from "@/shared/utils/formatter";

/** 라이더 구분 타입 */
export type RiderType = '일반' | '지점장' | '만65세이상';

/** 라이더 주정산내역 Domain Model */
export interface RiderSettlement {
    no: number;                             // 1. NO (UI 용)
    riderId: string;                        // 2. 라이더ID
    riderName: string;                      // 3. 라이더명
    riderType: RiderType;                   // 4. 라이더구분 (일반/지점장/만64세이상)
    settlementStartDate: string;            // 5. 정산시작일
    settlementEndDate: string;              // 6. 정산종료일
    processCount: number;                   // 7. 처리건수
    deliveryFee: number;                    // 8. 배달료
    additionalPayment: number;              // 9. 추가지급
    brokerManageFee: number;                // 10. 중개사운영수수료
    partnerOperationFee: number;            // 11. 협력사운영수수료
    totalOperationFee: number;              // 12. 총운영수수료 (중개사+협력사)
    taxableDeliveryFee: number;             // 13. 과세배달료 (고용산재 부과 대상 금액)
    monthlySalary: number;                  // 14. 월보수액 = ROUND(과세배달료 * 0.802, 0)
    riderEmployment: number;                // 15. 라이더_고용
    riderIndustrial: number;                // 16. 라이더_산재
    hourlyInsurance: number;                // 17. 시간제보험료
    insuranceTotal: number;                 // 18. 총합
    promotion: number;                      // 19. 프로모션 (Phase1: 0원, Phase2 반영)
    incomeTax: number;                      // 20. 소득세
    localTax: number;                       // 21. 지방세
    withholdingTax: number;                 // 22. 원천징수 (소득세+지방세)
    finalDeliveryFee: number;               // 23. 최종배달료
    advanceSettlement: number;              // 24. 선정산배달료
    hdcDeductAmt: number;                   // 25. 본사 일차감 (HDC_DEDUCT_VAL)
    sdcDeductAmt: number;                   // 26. 협력사 일차감 (SDC_DEDUCT_VAL)
    actualPayment: number;                  // 27. 실지급배달료
}

/** 라이더 주정산내역 DTO (백엔드 응답) - vr_rider_acct_weekly */
export interface RiderSettlementDto {
    seqNo: number;
    acctStDate: string;
    acctEdDate: string;
    supplyId: string;
    riderId: string;
    riderName?: string;
    riderType?: string;
    deliveryCnt?: number;
    deliveryVal: number;
    addVal: number;
    brokerManageFee: number;
    partnerManageFee: number;              // 협력사운영수수료
    totalManageFee: number;                // 총운영수수료
    taxableDeliveryVal: number;            // 과세배달료
    monthlySalary: number;                 // 월보수액
    riderEmpVal: number;                   // 라이더_고용
    riderAccidtVal: number;                // 라이더_산재
    timeInsurVal: number;                  // 시간제보험료
    insurTotVal: number;                   // 총합
    promotion: number;                     // 프로모션
    incomTax: number;                      // 소득세
    localTax: number;                      // 지방세
    withholdTax: number;                   // 원천징수
    finalDeliveryVal: number;              // 최종배달료
    preAcctVal: number;                    // 선정산배달료
    hdcDeductVal: number;                  // 본사 일차감
    sdcDeductVal: number;                  // 협력사 일차감
    totAcctVal: number;                    // 실지급배달료
    insDate: string;
    insUser: string;
    updDate: string | null;
    updUser: string | null;
}

/** 라이더 주정산내역 목록 조회 응답 DTO */
export interface GetRiderSettlementListResponseDto {
    totalCount: number;
    settlements: RiderSettlementDto[];
}

/** 최근 정산일자 조회 응답 DTO */
export interface GetLatestSettlementDateResponseDto {
    latestStartDate: string;    // 최근 정산시작일자
    latestEndDate: string;      // 최근 정산종료일자
}

/** API 응답 DTO */
export interface ApiResponseDto {
    success: boolean;
    message: string;
}

/** DTO -> Domain 변환 */
export const riderSettlementFromDto = (dto: RiderSettlementDto, index: number): RiderSettlement => {
    // 라이더 구분 (백엔드에서 판단하여 전달, 없으면 일반)
    const riderType = (dto.riderType as RiderType) || '일반';

    // 계산 필드들
    const taxableDeliveryFee = dto.taxableDeliveryVal || 0;
    const totalOperationFee = dto.totalManageFee || 0;
    const monthlySalary = dto.monthlySalary || 0;
    const riderEmployment = dto.riderEmpVal || 0;
    const riderIndustrial = dto.riderAccidtVal || 0;
    const incomeTax = dto.incomTax || 0;
    const localTax = dto.localTax || 0;
    const withholdingTax = dto.withholdTax || 0;

    // 총합 = 라이더고용 + 라이더산재 + 시간제보험료
    const insuranceTotal = dto.insurTotVal || 0;

    return {
        no: index + 1,
        riderId: dto.riderId,
        riderName: dto.riderName || dto.riderId,
        riderType: riderType,
        settlementStartDate: formatDateToDisplay(dto.acctStDate),
        settlementEndDate: formatDateToDisplay(dto.acctEdDate),
        processCount: dto.deliveryCnt || 0,
        deliveryFee: dto.deliveryVal || 0,
        additionalPayment: dto.addVal || 0,
        brokerManageFee: dto.brokerManageFee || 0,
        partnerOperationFee: dto.partnerManageFee || 0,
        totalOperationFee: totalOperationFee,
        taxableDeliveryFee: taxableDeliveryFee,
        monthlySalary: monthlySalary,
        riderEmployment: riderEmployment,
        riderIndustrial: riderIndustrial,
        hourlyInsurance: dto.timeInsurVal || 0,
        insuranceTotal: insuranceTotal,
        promotion: dto.promotion || 0,
        incomeTax: incomeTax,
        localTax: localTax,
        withholdingTax: withholdingTax,
        finalDeliveryFee: dto.finalDeliveryVal || 0,
        advanceSettlement: dto.preAcctVal || 0,
        hdcDeductAmt: dto.hdcDeductVal || 0,
        sdcDeductAmt: dto.sdcDeductVal || 0,
        actualPayment: dto.totAcctVal || 0
    };
};

/** 정산내역 목록 변환 */
export const riderSettlementListFromDto = (
    response: GetRiderSettlementListResponseDto
): RiderSettlement[] => {
    return response.settlements.map((dto, index) => riderSettlementFromDto(dto, index));
};

/** 검색 필터 상태 */
export interface FilterState {
    acctStDate: string;
    acctEdDate: string;
    riderName: string;
    supplierId: string;
}

/** 정렬 방향 */
export type SortDirection = 'asc' | 'desc' | null;

/** 정렬 설정 */
export interface SortConfig {
    columnIndex: number | null;
    direction: SortDirection;
}
